import {
  BALANCE,
  battleRuleSet,
  type BattlePlayerState,
  type BattleRoomState,
  type BattleState,
  type GameInput,
  type StepResult,
} from '@2k48/shared';

/** Convert the compact Colyseus schema into the pure ruleset state. */
export function roomStateToBattleState(
  room: BattleRoomState,
  lastMoveAt: ReadonlyMap<number, number> = new Map(),
): BattleState {
  const players: BattlePlayerState[] = Array.from(room.seats).map((seat) => ({
    seat: seat.seat,
    board: {
      size: BALANCE.board.size,
      nextTileId: seat.nextTileId,
      tiles: Array.from(seat.tiles).map((tile) => ({
        id: tile.id,
        value: tile.value,
        cell: { col: tile.col, row: tile.row },
      })),
    },
    rngState: seat.rngState,
    hp: seat.hp,
    score: seat.score,
    combo: seat.combo,
    maxCombo: seat.maxCombo,
    maxTile: seat.maxTile,
    moves: seat.moves,
    damageDealt: seat.damageDealt,
    damageTaken: seat.damageTaken,
    lastMoveAt: lastMoveAt.get(seat.seat) ?? 0,
    lastInputSeq: seat.lastInputSeq,
  }));

  return {
    seed: room.seed,
    status: players.some((player) => player.hp === 0) ? 'ko' : 'playing',
    players,
  };
}

export function predictInput(
  state: BattleState,
  input: GameInput,
  startedAt: number,
): StepResult<BattleState> {
  const context = {
    now: input.at,
    elapsed: Math.max(0, input.at - startedAt),
    random: () => 0,
    authoritative: false,
  } as const;
  const invalid = battleRuleSet.validate?.(state, input, context);
  if (invalid) {
    return { state, transitions: [], events: [], terminal: false, rejected: invalid };
  }
  return battleRuleSet.reduce(state, input, context);
}

/** Replay unacknowledged gestures on top of the newest authoritative patch. */
export function replayPending(
  authoritative: BattleState,
  inputs: readonly GameInput[],
  startedAt: number,
): BattleState {
  let state = authoritative;
  for (const input of inputs) {
    const step = predictInput(state, input, startedAt);
    if (!step.rejected) state = step.state;
  }
  return state;
}

/** Stable identity for deciding whether a hard DOM reconciliation is needed. */
export function playerFingerprint(player: BattlePlayerState | undefined): string {
  if (!player) return '';
  return [...player.board.tiles]
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((tile) => `${tile.id}:${tile.value}:${tile.cell.col},${tile.cell.row}`)
    .join('|');
}
